import type {ReactElement} from 'react';
import {useCallback, useRef, useState} from 'react';
import type {Editor} from 'grapesjs';
import {
  copyMarketingHtml,
  copyMarketingMediaQueries,
  copyMarketingText,
  copyTextComponentsJson,
  EmailMakerAdminPreviewSize,
  enableSelectionForAllComponents,
  exposeEditorOnWindow,
  isAutosaveOn,
  logProjectJson,
  openTemplateAdminTools,
  openTemplatePreviewDownload,
  restoreSelectionFromSnapshot,
  runAdminTestNoop,
  toggleAutosave,
} from '@EmailMakerReact/lib/email-maker-admin-tools-library';
import type {AdminSelectionSnapshot} from '@EmailMakerReact/lib/email-maker-admin-tools-library';
import {fixWrapperWidth} from '@EmailMakerReact/lib/email-maker-compatibility-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';
import {MODALS_IDS} from '@Components/modal-container';
import {openPanelInModal} from '@Components/modal-container/modal-container-reducer';
import {useAppDispatch} from '@/hooks';
import {ADMIN_CONTROLS_ICONS, ADMIN_CONTROLS_LABELS, autosaveLabel, selectionLabel} from '../../email-maker-admin-controls.types';
import {AdminControlButton} from '../admin-control-button/admin-control-button';

export interface GlobalAdminControlsProps {
  editor: Editor;
  idEmailTemplate: string | null;
}

interface AdminButtonDescriptor {
  key: string;
  label: string;
  icon?: string;
  onClick: () => void;
}

export function GlobalAdminControls({editor, idEmailTemplate}: GlobalAdminControlsProps): ReactElement {
  const dispatch = useAppDispatch();
  const [isSelectionEnabled, setIsSelectionEnabled] = useState(false);
  const [isAutosaveEnabled, setIsAutosaveEnabled] = useState(() => isAutosaveOn(editor));
  const selectionSnapshotRef = useRef<AdminSelectionSnapshot>({});

  const openAdminModal = useCallback(
    (modalId: string): void => {
      dispatch(openPanelInModal({modalId, panelProps: {}}));
    },
    [dispatch]
  );

  const handleToggleSelection = useCallback((): void => {
    if (isSelectionEnabled) {
      restoreSelectionFromSnapshot(editor, selectionSnapshotRef.current);
      selectionSnapshotRef.current = {};
      setIsSelectionEnabled(false);
      return;
    }
    selectionSnapshotRef.current = enableSelectionForAllComponents(editor);
    setIsSelectionEnabled(true);
  }, [editor, isSelectionEnabled]);

  const handleToggleAutosave = useCallback((): void => {
    setIsAutosaveEnabled(toggleAutosave(editor));
  }, [editor]);

  const runCopy = useCallback(
    (copy: (editor: Editor) => Promise<void>, label: string): void => {
      copy(editor).catch((e: unknown) => {
        logEmailMakerError(`admin copy failed: ${label}`, e);
      });
    },
    [editor]
  );

  const buttons: AdminButtonDescriptor[] = [
    {
      key: 'upload-custom-html',
      label: ADMIN_CONTROLS_LABELS.uploadCustomHtml,
      icon: ADMIN_CONTROLS_ICONS.rocket,
      onClick: (): void => {
        openAdminModal(MODALS_IDS.EMAIL_MAKER_ADMIN_UPLOAD_HTML_MODAL);
      },
    },
    {
      key: 'check-image-sources',
      label: ADMIN_CONTROLS_LABELS.checkImageSources,
      icon: ADMIN_CONTROLS_ICONS.rocket,
      onClick: (): void => {
        openAdminModal(MODALS_IDS.EMAIL_MAKER_ADMIN_IMAGE_SOURCES_MODAL);
      },
    },
    {
      key: 'copy-html',
      label: ADMIN_CONTROLS_LABELS.copyHtmlForMarketing,
      icon: ADMIN_CONTROLS_ICONS.copy,
      onClick: (): void => {
        runCopy(copyMarketingHtml, ADMIN_CONTROLS_LABELS.copyHtmlForMarketing);
      },
    },
    {
      key: 'copy-text',
      label: ADMIN_CONTROLS_LABELS.copyTextForMarketing,
      icon: ADMIN_CONTROLS_ICONS.copy,
      onClick: (): void => {
        runCopy(copyMarketingText, ADMIN_CONTROLS_LABELS.copyTextForMarketing);
      },
    },
    {
      key: 'copy-media-queries',
      label: ADMIN_CONTROLS_LABELS.copyMediaQueriesForMarketing,
      icon: ADMIN_CONTROLS_ICONS.copy,
      onClick: (): void => {
        runCopy(copyMarketingMediaQueries, ADMIN_CONTROLS_LABELS.copyMediaQueriesForMarketing);
      },
    },
    {
      key: 'copy-text-json',
      label: ADMIN_CONTROLS_LABELS.copyTextJson,
      icon: ADMIN_CONTROLS_ICONS.copy,
      onClick: (): void => {
        runCopy(copyTextComponentsJson, ADMIN_CONTROLS_LABELS.copyTextJson);
      },
    },
    {
      key: 'open-admin-tools',
      label: ADMIN_CONTROLS_LABELS.openAdminTools,
      icon: ADMIN_CONTROLS_ICONS.rocket,
      onClick: (): void => {
        openTemplateAdminTools(idEmailTemplate);
      },
    },
    {
      key: 'desktop-preview',
      label: ADMIN_CONTROLS_LABELS.desktopPreview,
      icon: ADMIN_CONTROLS_ICONS.download,
      onClick: (): void => {
        openTemplatePreviewDownload(idEmailTemplate, EmailMakerAdminPreviewSize.DESKTOP);
      },
    },
    {
      key: 'tablet-preview',
      label: ADMIN_CONTROLS_LABELS.tabletPreview,
      icon: ADMIN_CONTROLS_ICONS.download,
      onClick: (): void => {
        openTemplatePreviewDownload(idEmailTemplate, EmailMakerAdminPreviewSize.TABLET);
      },
    },
    {
      key: 'phone-preview',
      label: ADMIN_CONTROLS_LABELS.phonePreview,
      icon: ADMIN_CONTROLS_ICONS.download,
      onClick: (): void => {
        openTemplatePreviewDownload(idEmailTemplate, EmailMakerAdminPreviewSize.PHONE);
      },
    },
    {key: 'global-test', label: ADMIN_CONTROLS_LABELS.test, icon: ADMIN_CONTROLS_ICONS.rocket, onClick: runAdminTestNoop},
    {
      key: 'log-json',
      label: ADMIN_CONTROLS_LABELS.logJson,
      icon: ADMIN_CONTROLS_ICONS.rocket,
      onClick: (): void => {
        logProjectJson(editor);
      },
    },
    {key: 'toggle-selection', label: selectionLabel(isSelectionEnabled), icon: ADMIN_CONTROLS_ICONS.rocket, onClick: handleToggleSelection},
    {key: 'toggle-autosave', label: autosaveLabel(isAutosaveEnabled), icon: ADMIN_CONTROLS_ICONS.rocket, onClick: handleToggleAutosave},
    {
      key: 'expose-editor',
      label: ADMIN_CONTROLS_LABELS.exposeEditor,
      icon: ADMIN_CONTROLS_ICONS.rocket,
      onClick: (): void => {
        exposeEditorOnWindow(editor);
      },
    },
    {
      key: 'fix-wrapper-width',
      label: ADMIN_CONTROLS_LABELS.fixWrapperWidth,
      icon: ADMIN_CONTROLS_ICONS.rocket,
      onClick: (): void => {
        fixWrapperWidth(editor);
      },
    },
  ];

  return (
    <>
      {buttons.map((button) => (
        <AdminControlButton key={button.key} label={button.label} icon={button.icon} onClick={button.onClick} />
      ))}
    </>
  );
}
